import { isRouteErrorResponse } from 'react-router-dom';

import { APP_MODE, ViteAppMode } from '~/common/utils/env.util.ts';
import { isError } from '~/common/utils/type-guard.util.ts';
import ErrorPage from '~/pages/_error.tsx';

const UNKNOWN_ERROR_TEMPLATE = <ErrorPage message="Unknown Error" />;
const CHUNK_ERROR_PATTERN = /Loading chunk|dynamically imported module|Unexpected token/;
/* eslint-disable @typescript-eslint/naming-convention */
const DEFAULT_MESSAGES: Record<number, string> = {
  401: "You aren't authorized",
  404: 'Page Not Found',
};
/* eslint-enable @typescript-eslint/naming-convention */

const renderErrorPageByStatus = (status: number, fallbackMessage?: string) => {
  return (
    <ErrorPage
      message={DEFAULT_MESSAGES[status] ?? fallbackMessage ?? 'Unexpected Error'}
      status={status}
    />
  );
};

/**
 * * chunk file 로딩 관련 에러 여부 체크
 * @param error
 */
export const isChunkLoadingError = (error: unknown): boolean => {
  if (!isError(error)) return false;

  const { name = '', message = '' } = error;
  return name === 'TypeError' || CHUNK_ERROR_PATTERN.test(message);
};

export const isProductionChunkError = (error: unknown) => {
  return APP_MODE === ViteAppMode.PRODUCTION && isChunkLoadingError(error);
};

/**
 * * 권한, 조건 미충족 등 예외 상황에서 렌더링을 중단하고 적절한 에러 페이지를 반환합니다.
 * @param statusOrError unknown
 * @example
 * if (!user) return interruptWith(401);
 * if (!resourceExists) return interruptWith(404);
 * if (!hasPermission) return interruptWith(new AccessDeniedError());
 */
export function interruptWith(statusOrError: unknown) {
  // eslint-disable-next-line
  console.warn('[interrupt]', statusOrError);

  // ? 에러 상태 코드
  if (typeof statusOrError === 'number') {
    return renderErrorPageByStatus(statusOrError);
  }

  // ? 라우트 변경 중 에러
  if (isRouteErrorResponse(statusOrError)) {
    return renderErrorPageByStatus(statusOrError.status, statusOrError.data?.message);
  }

  // ? 일반 에러
  if (isError(statusOrError)) {
    return <ErrorPage title={statusOrError.name} message={statusOrError.message} />;
  }

  return UNKNOWN_ERROR_TEMPLATE;
}
